import { useEffect, useState } from "react";
import { useLenis } from "../components/LenisProvider"; // Adjust the import path

// Define the type for event data
interface Event {
  title: string;
  images: string[];
}

const eventsData: Event[] = [
  {
    title: "Grand Opening Celebration",
    images: [
      "/assets/imgs/images/nakshatra-nagar-1.webp",
      "/assets/imgs/images/nakshatra-nagar-2.webp",
      "/assets/imgs/images/nakshatra-nagar-3.webp",
      "/assets/imgs/images/nakshatra-nagar-4.webp",
      "/assets/imgs/images/nakshatra-nagar-1.webp",
      "/assets/imgs/images/nakshatra-nagar-2.webp",
      "/assets/imgs/images/nakshatra-nagar-3.webp",
      "/assets/imgs/images/nakshatra-nagar-4.webp",
    ],
  },
  {
    title: "Community Meet & Greet",
    images: [
      "/assets/imgs/images/nakshatra-nagar-1.webp",
      "/assets/imgs/images/nakshatra-nagar-2.webp",
      "/assets/imgs/images/nakshatra-nagar-3.webp",
      "/assets/imgs/images/nakshatra-nagar-4.webp",
      "/assets/imgs/images/nakshatra-nagar-1.webp",
      "/assets/imgs/images/nakshatra-nagar-2.webp",
      "/assets/imgs/images/nakshatra-nagar-3.webp",
      "/assets/imgs/images/nakshatra-nagar-4.webp",
    ],
  },
];

const Events: React.FC = () => {
  const lenis = useLenis();
  const [visibleItems, setVisibleItems] = useState<boolean[][]>(
    eventsData.map((event) => new Array(event.images.length).fill(false)),
  );
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Handle opening the image preview
  const openImagePreview = (image: string) => {
    setSelectedImage(image);
    document.body.style.overflow = "hidden";
    if (lenis) lenis.stop();
  };

  // Handle closing the image preview
  const closeImagePreview = () => {
    setSelectedImage(null);
    document.body.style.overflow = "";
    if (lenis) lenis.start();
  };

  // Scroll animation effect
  useEffect(() => {
    if (!lenis) return;

    const handleScroll = () => {
      const eventElements = document.querySelectorAll(".event-item");
      const newVisibleItems = eventsData.map((_, eventIndex) => {
        const imageElements =
          eventElements[eventIndex]?.querySelectorAll(".event-image");
        if (!imageElements)
          return new Array(eventsData[eventIndex].images.length).fill(false);
        return Array.from(imageElements).map((el) => {
          const rect = el.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom >= 0;
        });
      });
      setVisibleItems(newVisibleItems);
    };

    lenis.on("scroll", handleScroll);
    handleScroll();

    return () => {
      lenis.off("scroll", handleScroll);
      document.body.style.overflow = "";
    };
  }, [lenis]);

  return (
    <div className="min-h-screen" style={{ fontFamily: "Gambetta" }}>
      {/* Hero Section */}
      <section
        className="relative flex min-h-[75vh] w-full items-center justify-center"
        style={{
          backgroundImage: "url('/EventsBg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <h1
          className="animate-fade-in relative text-4xl font-bold tracking-widest text-white md:text-5xl"
          style={{ fontFamily: "HistoryPro" }}
        >
          Activity & Events
        </h1>
      </section>

      {/* Events Section */}
      <main className="py-12">
        <div className="mx-auto max-w-[95vw] px-4">
          {eventsData.map((event, eventIndex) => (
            <div key={eventIndex} className="event-item mb-12">
              {/* Event Title */}
              <div className="mb-6 flex items-center justify-center md:mb-12">
                <div className="relative">
                  <h1 className="text-3xl font-medium text-gray-800 transition-all duration-300 ease-in-out after:absolute after:-bottom-2 after:left-0 after:h-1 after:w-0 after:bg-[#1a4fa3] after:transition-all after:duration-300 hover:text-[#1a4fa3] hover:after:w-full">
                    {event.title}
                  </h1>
                </div>
              </div>

              {/* Event Images */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {event.images.map((image, imgIndex) => (
                  <div
                    key={imgIndex}
                    className={`event-image relative overflow-hidden rounded-lg shadow-xl transition-all duration-700 ease-in-out ${
                      visibleItems[eventIndex]?.[imgIndex]
                        ? "translate-y-0 opacity-100"
                        : "translate-y-10 opacity-0"
                    }`}
                    onClick={() => openImagePreview(image)}
                  >
                    <img
                      src={image}
                      alt={`${event.title} ${imgIndex + 1}`}
                      className="h-96 w-full object-cover transition-transform duration-500 ease-in-out hover:scale-105"
                      loading="lazy"
                    />
                    {/* <div className="absolute inset-0 bg-black/20 opacity-0 transition-opacity duration-300 hover:opacity-100"></div> */}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Custom Image Preview */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 transition-opacity duration-300 ease-in-out"
          onClick={closeImagePreview}
        >
          <div
            className="relative transform transition-transform duration-300 ease-in-out"
            style={{
              transform: selectedImage ? "scale(1)" : "scale(0.95)",
              opacity: selectedImage ? 1 : 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Full Size Preview"
              className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
